import ExternalLink from '@src/app-components/external-link/ExternalLink';
import InternalLink from '@src/app-components/internal-link/InternalLink';
import LinkButton from '@src/app-components/link/linkButton';
import Tooltip from '@src/app-components/tooltip/tooltip';
import { examplePlansFileList } from '@src/app-pages/Resources/components/Permitting/PermittingHelper';
import { FileTypes } from '@src/utils/enums';

export const permitInstructionsTooltips = {
  WOTUSDelinReport: (
    <>
      <p>
        The term "delineation" of <InternalLink title='WOTUS' href='/home/jurisdiction' content='WOTUS' /> is
        differentiated here from a "complete delineation report" of{' '}
        <InternalLink title='WOTUS' href='/home/jurisdiction' content='WOTUS' />. In order to submit a complete permit
        application, an applicant is only required to provide a delineation that depicts the boundaries of aquatic
        resources on project site. This information will assist the agencies in making meaningful comments during the
        public notice period.
      </p>
      <p>
        A "complete delineation report" includes the boundary information in addition to the scientific methods and
        documentation used to arrive at those boundaries. A "complete delineation report" is typically in the form of a
        report that consists of a document or series of documents, stored in the USACE administrative record for the
        project, that describes the methodology used to conduct the delineation and typically includes a collection of
        one or more maps, datasheets, photographs, and/or figures that depict and support a legally defensible
        delineation of <InternalLink title='WOTUS' href='/home/jurisdiction' content='WOTUS' /> on a property.
      </p>
    </>
  ),
  CompeteDelinReport: (
    <p>
      A "complete delineation report" is often in the form of a report that consists of a document or series of
      documents, stored in the USACE administrative record for the project, that describes the methodology used to
      conduct the delineation and typically includes a collection of one or more maps, datasheets, photographs, and/or
      figures that depict and support a legally defensible delineation of{' '}
      <InternalLink title='WOTUS' href='/home/jurisdiction' content='WOTUS' /> on a property.{' '}
    </p>
  ),
  PGP: 'Nationwide Permits (NWPs), Regional General Permits (RGPs), and Programmatic General Permits (PGPs) are types of permits whose purpose is to authorize activities that the USACE has determined to be substantially similar in nature (such as utility lines, residential developments, bank stabilization, etc.) and which cause minimal individual and cumulative environmental impacts. Regional General Permits can authorize activities within a limited geographic area (such as a county or state), a specific region (such as a group of contiguous states), or the entire nation (Nationwide General Permits).',
  RegionalConditions:
    'Regional Conditions are District-specific conditions that apply to Nationwide Permits within the geographic area of responsibility of the USACE District where the project is located. Regional Conditions account for regional issues to ensure that the NWP activity will not result in more than minimal adverse environmental effects either individually or cumulatively.',
  WOTUS:
    'Waters of the United States that are permanently adversely affected by filling, flooding, excavation, or drainage because of the regulated activity. The loss of stream bed includes the acres of stream bed that are permanently adversely affected by filling or excavation because of the regulated activity. Permanent adverse effects include permanent discharges of dredged or fill material that change an aquatic area to dry land, increase the bottom elevation of a waterbody, or change the use of a waterbody. The acreage of loss of waters of the United States is a threshold measurement of the impact to jurisdictional waters or wetlands for determining whether a project may qualify for an NWP; it is not a net threshold that is calculated after considering compensatory mitigation that may be used to offset losses of aquatic functions and services. Waters of the United States temporarily filled, flooded, excavated, or drained, but restored to pre-construction contours and elevations after construction, are not included in the measurement of loss of waters of the United States. Impacts resulting from activities that do not require Department of the Army authorization, such as activities eligible for exemptions under Section 404(f) of the Clean Water Act, are not considered when calculating the loss of waters of the United States.',
};

export const currentNwpLinks = [
  {
    href: 'https://usace.contentdm.oclc.org/utils/getfile/collection/p16021coll9/id/3209',
    content: '2026 Nationwide Permit List, General Conditions, and Definitions',
  },
  {
    href: 'https://www.regulations.gov/search?filter=COE-2020-0002',
    content: '2026 Nationwide Permit Regional Conditions',
  },
  {
    href: 'https://usace.contentdm.oclc.org/utils/getfile/collection/p16021coll9/id/3211',
    content: '2026 Nationwide Permits Summary Chart',
  },
];

export const activityRequirementsAccordion = [
  {
    id: 1,
    headingLevel: 'h3',
    title: 'Activities Involving Discharges of Dredged or Fill Material',
    content: (
      <>
        <p>
          The application must include the source of the material; the purpose of the discharge, a description of the
          type, composition and quantity of the material; the method of transportation and disposal of the material; and
          the location of the disposal site (33 CFR 325.1(d)(4)).
        </p>
        <p>Mitigation:</p>
        <p>
          The application{' '}
          <span className='text-bold'>
            must include a statement describing how impacts to{' '}
            <InternalLink title='WOTUS' href='/home/jurisdiction' content='WOTUS' openInNewTab /> are to be avoided and
            minimized. The application must also include a statement describing how impacts to WOTUS are to be
            compensated or a statement explaining why compensatory mitigation should not be required
          </span>{' '}
          for the proposed impacts (33 CFR 325.1(d)(7)). This explanation shall address the proposed avoidance and
          minimization and the amount, type, and location of any proposed compensatory mitigation, including any
          out-of-kind compensation, or indicate an intention to use an approved mitigation bank or in-lieu fee program.
        </p>
        <p>
          If it has been determined compensatory mitigation may be required and an approved mitigation bank or in-lieu
          fee program will not be used, applicants have the option to submit a{' '}
          <span className='text-bold'>draft compensatory mitigation plan</span> prepared in accordance with 33 CF
          332.4(c) with the permit application.
        </p>
      </>
    ),
  },
  {
    id: 2,
    headingLevel: 'h3',
    title: 'Activities Involving Dredging in Navigable Waters of the United States',
    content: (
      <p>
        The application must include a description of the type, composition, and quantity of the material to be dredged,
        the method of dredging, and the site and plans for disposal of the dredged material (33 CFR 325.1(d)(3)).
      </p>
    ),
  },
  {
    id: 3,
    headingLevel: 'h3',
    title: 'Activities occurring in the Territorial Seas or Ocean Waters',
    content: (
      <p>
        The application must include a description of the activity's relationship to the baseline from which the
        territorial sea is measured (33 CFR 325.3(a)(7)).
      </p>
    ),
  },
  {
    id: 4,
    headingLevel: 'h3',
    title:
      'Activities Involving the Transportation of Dredged Material for the Purpose of Disposing of it in Ocean Waters ',
    content: (
      <p>
        The application must include the source of the material; the purpose of the discharge, a description of the
        type, composition, and quantity of the material; the method of transportation and disposal of the material; and
        the location of the disposal site (33 CFR 325.1(d)(4)). The application must also include the physical
        boundaries of the disposal site, a description of known dredged material discharges at the proposed disposal
        site, the existence and documented effects of other authorized disposals that have been made in the disposal
        area (e.g., heavy metal background reading and organic carbon content), and the estimated length of time during
        which the disposal would continue at the proposed site. If the proposed disposal site has not been designated by
        the Administrator, Environmental Protection Agency, the application must include a description of the
        characteristics of the proposed disposal site and an explanation as to why no previously designated disposal
        site is feasible. See 33 CFR 325.3(a)(17).
      </p>
    ),
  },
  {
    id: 5,
    headingLevel: 'h3',
    title: 'Activities Involving the Construction of a Filled Area or Pile or Float-supported Platform',
    content: (
      <p>
        The project description must include the use of, and specific structures to be erected on, the fill or platform
        (33 CFR 325.1(d)(5)).
      </p>
    ),
  },
  {
    id: 6,
    headingLevel: 'h3',
    title: 'Activities Involving the Construction or Placement of an Artificial Reef',
    content: (
      <p>
        The application must include provisions for siting, constructing, monitoring, and managing the artificial reef
        (33 CFR 325.1(d)(9)).
      </p>
    ),
  },
];

export const faqAccordion = [
  {
    id: '1',
    headingLevel: 'h3',
    title: 'When can I start work?',
    content: (
      <>
        <p>For General Permits, the applicant shall not begin the proposed activity until either: </p>
        <p>
          1. He or she is notified in writing by the USACE District that the activity may proceed under the NWP with any
          special conditions imposed by the district or division engineer; or{' '}
        </p>
        <p>
          2. 45 calendar days have passed from the USACE District's receipt of the complete PCN and the applicant has
          not received written notice from the district or division engineer. However, if the permittee was required to
          notify USACE pursuant to general condition 18 that listed species or critical habitat might be affected or are
          in the vicinity of the activity, or to notify USACE pursuant to general condition 20 that the activity might
          have the potential to cause effects to historic properties, the permittee cannot begin the activity until
          receiving written notification from USACE that there is "no effect" on listed species or "no potential to
          cause effects" on historic properties, or that any consultation required under Section 7 of the Endangered
          Species Act (see 33 CFR 330.4(f)) and/or Section 106 of the National Historic Preservation Act (see 33 CFR
          330.4(g)) has been completed. If the proposed activity requires a written waiver to exceed specified limits of
          an NWP, the permittee may not begin the activity until the USACE District issues the waiver. If the district
          or division engineer notifies the permittee in writing that an Individual Permit is required within 45
          calendar days of receipt of a complete PCN, the permittee cannot begin the activity until an Individual Permit
          has been issued. Subsequently, the permittee's right to proceed under the NWP may be modified, suspended, or
          revoked only in accordance with the procedure set forth in 33 CFR 330.5(d)(2).{' '}
        </p>
      </>
    ),
  },
  {
    id: '2',
    headingLevel: 'h3',
    title: 'What does permanent "loss" of waters mean?',
    content: (
      <p>
        Waters of the United States that are permanently adversely affected by filling, flooding, excavation, or
        drainage because of the regulated activity. The loss of stream bed includes the acres of stream bed that are
        permanently adversely affected by filling or excavation because of the regulated activity. Permanent adverse
        effects include permanent discharges of dredged or fill material that change an aquatic area to dry land,
        increase the bottom elevation of a waterbody, or change the use of a waterbody. The acreage of loss of waters of
        the United States is a threshold measurement of the impact to jurisdictional waters or wetlands for determining
        whether a project may qualify for an NWP; it is not a net threshold that is calculated after considering
        compensatory mitigation that may be used to offset losses of aquatic functions and services. Waters of the
        United States temporarily filled, flooded, excavated, or drained, but restored to pre-construction contours and
        elevations after construction, are not included in the measurement of loss of waters of the United States.
        Impacts resulting from activities that do not require Department of the Army authorization, such as activities
        eligible for exemptions under Section 404(f) of the Clean Water Act, are not considered when calculating the
        loss of waters of the United States.{' '}
      </p>
    ),
  },
  {
    id: '3',
    headingLevel: 'h3',
    title: 'How does the USACE decide whether to allow my project to use a Nationwide Permit?',
    content: (
      <>
        <p>
          The USACE District will make the decision whether to allow the use of a specific Nationwide Permit as
          follows:{' '}
        </p>
        <p>
          1. In reviewing the PCN for the proposed activity, the USACE District will determine whether the activity
          authorized by the NWP will result in more than minimal individual or cumulative adverse environmental effects
          or may be contrary to the public interest. If a project proponent requests authorization by a specific NWP,
          the USACE District should issue the NWP verification for that activity if it meets the terms and conditions of
          that NWP, unless they determine, after considering mitigation, that the proposed activity will result in more
          than minimal individual and cumulative adverse effects on the aquatic environment and other aspects of the
          public interest and exercises discretionary authority to require an Individual Permit for the proposed
          activity. For a linear project, this determination will include an evaluation of the single and complete
          crossings of waters of the United States that require PCNs to determine whether they individually satisfy the
          terms and conditions of the NWP(s), as well as the cumulative effects caused by all of the crossings of waters
          of the United States authorized by an NWP. If an applicant requests a waiver of an applicable limit, as
          provided for in NWPs 13, 36, or 54, the USACE District will only grant the waiver upon a written determination
          that the NWP activity will result in only minimal individual and cumulative adverse environmental
          effects.{' '}
        </p>
        <p>
          2. When making minimal adverse environmental effects determinations the USACE District will consider the
          direct and indirect effects caused by the NWP activity. They will also consider the cumulative adverse
          environmental effects caused by activities authorized by an NWP and whether those cumulative adverse
          environmental effects are no more than minimal. The USACE District will also consider site specific factors,
          such as the environmental setting in the vicinity of the NWP activity, the type of resource that will be
          affected by the NWP activity, the functions provided by the aquatic resources that will be affected by the NWP
          activity, the degree or magnitude to which the aquatic resources perform those functions, the extent that
          aquatic resource functions will be lost as a result of the NWP activity (e.g., partial or complete loss
          <Tooltip name='WOTUSTooltip' content={permitInstructionsTooltips.WOTUS} />
          ), the duration of the adverse effects (temporary or permanent), the importance of the aquatic resource
          functions to the region (e.g., watershed or ecoregion), and mitigation required by the USACE District. If an
          appropriate functional or condition assessment method is available and practicable to use, that assessment
          method may be used by the USACE District to assist in the minimal adverse environmental effects determination.
          The USACE District may add case-specific special conditions to the NWP authorization to address site-specific
          environmental concerns.
        </p>
        <p>
          3. If the proposed activity requires a PCN and will result in a loss
          <Tooltip name='WOTUSTooltip' content={permitInstructionsTooltips.WOTUS} /> of greater than 1/10 acre of
          wetlands or 3/100-acre of stream bed, the applicant should submit a mitigation proposal with the PCN.
          Applicants may also propose compensatory mitigation for NWP activities with smaller impacts, or for impacts to
          other types of waters. The USACE District will consider any proposed compensatory mitigation or other
          mitigation measures the applicant has included in the proposal in determining whether the net adverse
          environmental effects of the proposed activity are no more than minimal. The compensatory mitigation proposal
          may be either conceptual or detailed. If the USACE District determines that the activity complies with the
          terms and conditions of the NWP and that the adverse environmental effects are no more than minimal, after
          considering mitigation, the USACE District will notify the permittee and include any activity-specific
          conditions in the NWP verification the USACE District deems necessary. Conditions for compensatory mitigation
          requirements must comply with the appropriate provisions at 33 CFR 332.3(k). The USACE District must approve
          the final mitigation plan before the permittee commences work in waters of the United States, unless the USACE
          District determines that prior approval of the final mitigation plan is not practicable or not necessary to
          ensure timely completion of the required compensatory mitigation. If the applicant elects to submit a
          compensatory mitigation plan with the PCN, the USACE District will expeditiously review the proposed
          compensatory mitigation plan. The USACE District must review the proposed compensatory mitigation plan within
          45 calendar days of receiving a complete PCN and determine whether the proposed mitigation would ensure that
          the NWP activity results in no more than minimal adverse environmental effects. If the net adverse
          environmental effects of the NWP activity (after consideration of the mitigation proposal) are determined by
          the USACE District to be no more than minimal, the USACE District will provide a timely written response to
          the applicant. The response will state that the NWP activity can proceed under the terms and conditions of the
          NWP, including any activity-specific conditions added to the NWP authorization by the USACE District.{' '}
        </p>
        <p>
          4. If the USACE District determines that the adverse environmental effects of the proposed activity are more
          than minimal, then the USACE District will notify the applicant either: (a) that the activity does not qualify
          for authorization under the NWP and instruct the applicant on the procedures to seek authorization under an
          Individual Permit; (b) that the activity is authorized under the NWP subject to the applicant's submission of
          a mitigation plan that would reduce the adverse environmental effects so that they are no more than minimal;
          or (c) that the activity is authorized under the NWP with specific modifications or conditions. Where the
          USACE District determines that mitigation is required to ensure no more than minimal adverse environmental
          effects, the activity will be authorized within the 45-day PCN period (unless additional time is required to
          comply with general conditions 18, 20, and/or 31), with activity-specific conditions that state the mitigation
          requirements. The authorization will include the necessary conceptual or detailed mitigation plan or a
          requirement that the applicant submit a mitigation plan that would reduce the adverse environmental effects so
          that they are no more than minimal. When compensatory mitigation is required, no work in waters of the United
          States may occur until the USACE District has approved a specific mitigation plan or has determined that prior
          approval of a final mitigation plan is not practicable or not necessary to ensure timely completion of the
          required compensatory mitigation.{' '}
        </p>
      </>
    ),
  },
];

export const commonRequirementsAccordion = (doDownloadFile) => [
  {
    id: 1,
    headingLevel: 'h3',
    title: 'Project Description and Nature of Activity',
    content: (
      <>
        <p className='text-bold'>
          Providing a clear description of the project can help avoid unnecessary agency comments during the public
          notice period.
        </p>
        <p>Project Description:</p>
        <p>Describe the proposed activity, including:</p>
        <ul>
          <li>Purpose of the proposed activity and its intended use.</li>
          <li>
            Detailed description of the direct and indirect adverse environmental effects the activity would cause.
          </li>
          <li>
            Surface area of wetlands or other waters proposed to be filled, including temporary and permanent impacts.
          </li>
          <li>Dimensions of any structures (such as wing walls, buildings, docks, etc.) and excavation areas.</li>
          <li>
            Detailed discussion on how the project has been designed to avoid and minimize impacts to{' '}
            <InternalLink title='WOTUS' href='/home/jurisdiction' content='WOTUS' openInNewTab /> to the maximum extent
            practicable, including previous designs, considerations, and limiting site factors.
          </li>
          <li>Proposed mitigation measures.</li>
          <li>Proposed construction materials and methods by which the work is to be completed.</li>
          <li>Any related activities to be developed as the result of the proposed project.</li>
          <li>
            Any other General or Individual Permits used or intended to be used to authorize any part of the proposed
            project or any related activity.
          </li>
          <li>Approximate construction timeline and duration of any temporary fills or structures.</li>
        </ul>
        <p>
          Please note: for single and complete linear projects (roadways, utility lines, etc.) the application must
          include the quantity of anticipated losses of wetlands, other special aquatic sites, and other waters for each
          single and complete crossing of those waters.
        </p>
        <p>Nature of Activity:</p>
        <p>
          Describe the overall activity or project. Give appropriate dimensions of structures/fills such as wing walls,
          dikes, (identify the materials to be used in construction, as well as the methods by which the work is to be
          done), or excavation (length, width, and depth). If the project would include the construction of a filled or
          pile or float-supported platform, you must include a description of the use of, and specific structures to be
          erected on, the fill or platform.
        </p>
      </>
    ),
  },
  {
    id: 2,
    headingLevel: 'h3',
    title: 'Project Plans',
    content: (
      <>
        <p>
          The application{' '}
          <span className='text-bold'>
            must include plan and elevation drawings showing the general and specific site location and character of all
            proposed activities, including the size relationship of the proposed structures to the size of the impacted
            waterway and depth of water in the area (33 CFR 325.3(a)(6))
          </span>
          . An original set of <span className='text-bold'>8½ x11 or 11 ½ x 17-inch</span> plans including a vicinity
          map, a plan view, and a typical cross-section map should be provided in the Supporting Information Section.
        </p>
        <p>
          Sketches/plans should be provided when necessary to show that the activity complies with the terms of the
          applicable NWP. This information can greatly facilitate timely reviews and decisions by USACE. Sketches/plans
          should contain sufficient detail to provide an illustrative description of the proposed activity (e.g., a
          conceptual plan), but do not need to be detailed engineering plans.{' '}
        </p>
        <p>
          While plans do not need to be professionally produced, they should be clear, accurate, and contain all
          necessary information. The example plans included in these instructions could be used as a guide when
          developing project plans.{' '}
          <span className='text-bold'>
            Following these examples will help facilitate an efficient review of the application and prevent the need
            for the USACE to request additional information.
          </span>
        </p>
        <p className='text-bold'>Example Plans:</p>
        {examplePlansFileList.map((file, index) => (
          <p key={index} className={`margin-bottom-0 ${index !== 0 && 'margin-top-0'}`}>
            <LinkButton
              onClick={() => doDownloadFile(file.filename, FileTypes.Resource)}
              title={file.label}
              content={file.label}
            />
          </p>
        ))}
      </>
    ),
  },
  {
    id: 3,
    headingLevel: 'h3',
    title: 'Aquatic Resources Delineation',
    content: (
      <>
        <p>
          A delineation of aquatic resources must be prepared in accordance with USACE-approved scientific methods to
          identify and locate all wetlands, streams, lakes, ponds, and other waters within the project area. The aquatic
          resources must be clearly depicted on project drawings and plans, in order to clearly convey the extent and
          nature of the proposed activity, and support the required explanation of how impacts to WOTUS are being
          avoided and minimized on the project site, in accordance with 33 CFR 325.1(d)(7).
        </p>
        <p>
          While it is the USACE's role to identify Waters of the U.S. on a project site, many districts lack the staff
          and capacity to conduct delineations for every project. This is especially true for large or complex sites
          with numerous aquatic features. Therefore, it is highly recommended that the applicant voluntarily retain an
          environmental consulting firm to prepare a delineation report on their behalf. The applicant may request that
          the USACE perform the delineation but be aware that this could lead to significant delays in the review of
          their application. Please contact your local USACE Regulatory office to determine if they conduct delineations
          and to confirm current processing times.
        </p>
        <p>
          To expedite the review process, the USACE recommends submission of a complete delineation report either prior
          to or concurrently with the permit application. Failure to provide a complete report may result in it being
          requested as additional information, pursuant to 33 CFR 325.1(e), 40 CFR 230.5(d), and 40 CFR 230.5(c). The
          permit review clock does not start until a complete delineation has been submitted or completed by the USACE.
        </p>
      </>
    ),
  },
  {
    title: 'Mitigation',
    content: (
      <p>
        The activity must be designed and constructed to avoid and minimize adverse effects, both temporary and
        permanent, to waters of the United States to the maximum extent practicable at the project site (i.e., on site).
        For General Permits, if the proposed activity will result in the loss
        <Tooltip name='WOTUSTooltip' content={permitInstructionsTooltips.WOTUS} /> of greater than 1/10-acre of wetlands
        or 3/100-acre of stream bed and a PCN is required, the applicant must submit a statement describing how the
        mitigation requirement will be satisfied, or explaining why the adverse environmental effects are no more than
        minimal and why compensatory mitigation should not be required. As an alternative, the applicant may submit a
        conceptual or detailed{' '}
        <ExternalLink
          href='https://www.usace.army.mil/Missions/Civil-Works/Regulatory-Program-and-Permits/mitig_info/'
          content='mitigation plan'
        />
        .{' '}
      </p>
    ),
    id: 4,
    headingLevel: 'h3',
  },
  {
    title: 'Threatened and Endangered Species',
    content: (
      <>
        <p>
          For non-federal permittees, if any listed species (or species proposed for listing) or designated critical
          habitat (or critical habitat proposed for such designation) might be affected or is in the vicinity of the
          activity, or if the activity is located in designated critical habitat (or critical habitat proposed for such
          designation), the application must include the name(s) of those endangered or threatened species (or species
          proposed for listing) that might be affected by the proposed activity or utilize the designated critical
          habitat (or critical habitat proposed for such designation) that might be affected by the proposed activity.
          For NWP activities that require pre-construction notification, Federal permittees must provide documentation
          demonstrating compliance with the Endangered Species Act.{' '}
        </p>
        <p>
          For more information on threatened and endangered species, visit{' '}
          <ExternalLink
            href='https://www.fisheries.noaa.gov/national/habitat-conservation/essential-fish-habitat'
            title='Essential Fish Habitat'
            content='Essential Fish Habitat'
          />
          and{' '}
          <ExternalLink
            href='https://www.fws.gov/service/esa-section-7-consultation'
            title='Endangered Species'
            content='Endangered Species'
          />{' '}
          resources.
        </p>
      </>
    ),
    id: 5,
    headingLevel: 'h3',
  },
  {
    title: 'Historic Properties and Cultural Resources',
    content: (
      <>
        <p>
          For non-federal permittees, if the activity might have the potential to cause effects to a historic property
          listed on, determined to be eligible for listing on, or potentially eligible for listing on, the National
          Register of Historic Places, the application must state which historic property might have the potential to be
          affected by the proposed activity or include a vicinity map indicating the location of the historic property.
          For NWP activities that require pre-construction notification, Federal permittees must provide documentation
          demonstrating compliance with Section 106 of the National Historic Preservation Act.{' '}
        </p>
        <p>
          For more information, visit{' '}
          <ExternalLink
            href='https://www.usace.army.mil/Missions/Civil-Works/Regulatory-Program-and-Permits/Related-Resources/'
            title='historic properties and cultural resources'
            content='historic properties and cultural resources'
          />
          .
        </p>
      </>
    ),
    id: 6,
    headingLevel: 'h3',
  },
  {
    title: 'Wild and Scenic Rivers',
    content: (
      <p>
        For an activity that will occur in a component of the National Wild and Scenic River System, or in a river
        officially designated by Congress as a "study river" for possible inclusion in the system while the river is in
        an official study status, the application must identify the Wild and Scenic River or the "study river" (see
        general condition 16).{' '}
      </p>
    ),
    id: 7,
    headingLevel: 'h3',
  },
  {
    title: 'Federally Authorized Civil Works Projects',
    content: (
      <p>
        For an activity that requires permission from, or review by, USACE pursuant to 33 U.S.C. 408 because it will
        alter or temporarily or permanently occupy or use a U.S. Army Corps of Engineers federally authorized civil
        works project, the preconstruction notification must include a statement confirming that the project proponent
        has submitted a written request for Section 408 permission from, or review by, the District office having
        jurisdiction over that USACE project.{' '}
      </p>
    ),
    id: 8,
    headingLevel: 'h3',
  },
];
